import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  decide,
  FITZPATRICK_SKIN_TYPES,
  IPLEDGE_RISK_CATEGORIES,
  PATCH_READINGS,
  PATCH_READ_DAYS,
  type DermatologyProfileResponse,
  type DestructionProcedureResponse,
  type PatchTestPanelResponse,
  type RecallStatusResponse,
} from '@emr/shared';

import {
  addPatchTestReading,
  createDestructionProcedure,
  createPatchTestPanel,
  fetchDermatologyProfile,
  fetchDestructionProcedures,
  fetchPatchTestPanels,
  fetchRecallStatuses,
  recordSkinExam,
  updateDermatologyProfile,
} from '../api/dermatology.ts';
import { useGrants } from '../auth/sessionContext.ts';
import { FailureNotice, LoadingNotice } from '../components/AsyncState.tsx';
import { Button, ErrorText, Field, Panel } from '../components/primitives.tsx';
import { GatedRoute } from '../components/GatedRoute.tsx';

/**
 * The dermatology module's real, patient-scoped surface — `docs/comparison-emrx.md` §5.2/§5.3,
 * `BACKLOG.md` M3. Reachable directly by URL even with the nav hidden or the catalog entry showing
 * "coming soon" for an unlicensed org, per `GatedPages.tsx`'s own stated reasoning: a route cannot
 * rely on nav visibility, so the gate is re-applied here via the same `GatedRoute` component.
 *
 * Four sections, each independently queried and independently writable behind `chart:write`:
 *
 *   - **Guardrail profile** — the two patient-recorded-answer fields (`patientAssessment.ts`).
 *     Never a demographic-derived default; the selects below start blank until a clinician answers.
 *   - **Recall / surveillance** — `computeRecallStatuses`'s §6.1-fixed output, one row per active,
 *     coded diagnosis, plus a way to record a skin exam (the discharge event for a surveillance rule).
 *   - **Patch testing** — panels and their readings, with the day-2 guard enforced entirely by
 *     `reportableReading`/`isReportablePositive` on the server; this page only displays what comes
 *     back.
 *   - **Destruction procedures** — lesion count in, CPT lines (or an explicit refusal) out, from
 *     `destructionCptCodes`.
 */
export function PatientDermatologyPage() {
  const { t } = useTranslation('dermatology');
  const { patientId } = useParams<{ patientId: string }>();

  if (patientId === undefined) {
    return <FailureNotice error={new Error('No patient id in the URL.')} />;
  }

  return (
    <GatedRoute title={t('heading')} capability="chart:read" feature="dermatologyModule">
      <div className="flex flex-col gap-6">
        <ProfileSection patientId={patientId} />
        <RecallSection patientId={patientId} />
        <PatchTestSection patientId={patientId} />
        <DestructionSection patientId={patientId} />
      </div>
    </GatedRoute>
  );
}

function SectionHeading({ children }: { readonly children: string }) {
  return <h2 className="m-0 text-lg font-semibold text-ink">{children}</h2>;
}

/**
 * The loading/error/empty/content chain every section below needs, in one place. Found in review
 * 2026-08-18: all four sections reimplemented the identical four-way ternary by hand. `isEmpty`
 * defaults to "never empty" for a section like `ProfileSection` whose query returns a single record
 * rather than a list — there is no distinct empty state to show once it has loaded.
 */
function QuerySection<T>({
  query,
  loadingLabel,
  emptyMessage,
  isEmpty = () => false,
  children,
}: {
  readonly query: UseQueryResult<T>;
  readonly loadingLabel: string;
  readonly emptyMessage?: string;
  readonly isEmpty?: (data: T) => boolean;
  readonly children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <LoadingNotice label={loadingLabel} />;
  if (query.isError) {
    return (
      <FailureNotice
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  if (emptyMessage !== undefined && isEmpty(query.data)) {
    return <p className="m-0 text-sm text-ink-muted">{emptyMessage}</p>;
  }
  return <>{children(query.data)}</>;
}

// --- Guardrail profile ---------------------------------------------------------------------------

function ProfileSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('dermatology');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['dermatology', 'profile', patientId];

  const query = useQuery<DermatologyProfileResponse>({
    queryKey,
    queryFn: ({ signal }) => fetchDermatologyProfile(patientId, signal),
  });

  const [phototype, setPhototype] = useState('');
  const [riskCategory, setRiskCategory] = useState('');

  const save = useMutation({
    mutationFn: () => {
      const input: {
        fitzpatrickSkinType?: (typeof FITZPATRICK_SKIN_TYPES)[number];
        ipledgeRiskCategory?: (typeof IPLEDGE_RISK_CATEGORIES)[number];
      } = {};
      if (isFitzpatrickInput(phototype)) input.fitzpatrickSkinType = phototype;
      if (isIpledgeInput(riskCategory)) input.ipledgeRiskCategory = riskCategory;
      return updateDermatologyProfile(patientId, input);
    },
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKey, profile);
      setPhototype('');
      setRiskCategory('');
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('profile.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('profile.description')}</p>
      <Panel>
        <QuerySection query={query} loadingLabel={t('common.loading')}>
          {(profile) => (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-ink-muted">{t('profile.recordedPhototype')}</span>
                  <span className="text-sm font-medium text-ink">
                    {profile.fitzpatrickSkinType === null
                      ? t('profile.notRecorded')
                      : t('profile.phototypeType', { type: profile.fitzpatrickSkinType })}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-ink-muted">
                    {t('profile.recordedRiskCategory')}
                  </span>
                  <span className="text-sm font-medium text-ink">
                    {profile.ipledgeRiskCategory === null
                      ? t('profile.notRecorded')
                      : ipledgeLabel(profile.ipledgeRiskCategory, t)}
                  </span>
                </div>
              </div>

              {mayWrite ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    save.mutate();
                  }}
                  className="flex flex-col gap-3 border-t border-row-line pt-3 sm:flex-row sm:items-end sm:gap-4"
                >
                  <label className="flex flex-1 flex-col gap-1.5">
                    <span className="text-sm font-medium text-ink">
                      {t('profile.recordPhototypeLabel')}
                    </span>
                    <select
                      value={phototype}
                      onChange={(event) => {
                        setPhototype(event.target.value);
                      }}
                      className="w-full rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                    >
                      <option value="">{t('profile.notAnswered')}</option>
                      {FITZPATRICK_SKIN_TYPES.map((value) => (
                        <option key={value} value={value}>
                          {t('profile.phototypeType', { type: value })}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-1 flex-col gap-1.5">
                    <span className="text-sm font-medium text-ink">
                      {t('profile.recordRiskCategoryLabel')}
                    </span>
                    <select
                      value={riskCategory}
                      onChange={(event) => {
                        setRiskCategory(event.target.value);
                      }}
                      className="w-full rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                    >
                      <option value="">{t('profile.notAnswered')}</option>
                      {IPLEDGE_RISK_CATEGORIES.map((value) => (
                        <option key={value} value={value}>
                          {ipledgeLabel(value, t)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Button
                    type="submit"
                    pending={save.isPending}
                    disabled={phototype === '' && riskCategory === ''}
                  >
                    {t('profile.save')}
                  </Button>
                </form>
              ) : null}
              {save.isError ? <ErrorText message={t('profile.saveError')} /> : null}
            </div>
          )}
        </QuerySection>
      </Panel>
    </section>
  );
}

function isFitzpatrickInput(value: string): value is (typeof FITZPATRICK_SKIN_TYPES)[number] {
  return (FITZPATRICK_SKIN_TYPES as readonly string[]).includes(value);
}
function isIpledgeInput(value: string): value is (typeof IPLEDGE_RISK_CATEGORIES)[number] {
  return (IPLEDGE_RISK_CATEGORIES as readonly string[]).includes(value);
}
/** Takes `t` as a parameter — a plain function, not a component or hook, so it cannot call
 * `useTranslation` itself (same pattern as `LoginPage.tsx`'s `loginErrorMessage`). */
function ipledgeLabel(value: string, t: TFunction<'dermatology'>): string {
  return value === 'can_become_pregnant'
    ? t('profile.canBecomePregnant')
    : t('profile.cannotBecomePregnant');
}

// --- Recall / surveillance -------------------------------------------------------------------------

/** Maps the API's snake_case state value to its `recall.state.*` key — kept separate from a
 * component-scoped label map so it works from the plain `t()` call below without needing its own
 * `TFunction`-parameterised helper. */
function recallStateKey(
  state: RecallStatusResponse['state'],
): 'overdue' | 'dueSoon' | 'scheduledLater' {
  if (state === 'overdue') return 'overdue';
  if (state === 'due_soon') return 'dueSoon';
  return 'scheduledLater';
}

function RecallSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('dermatology');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['dermatology', 'recall', patientId];

  const query = useQuery<readonly RecallStatusResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchRecallStatuses(patientId, signal),
  });

  const [icd10Code, setIcd10Code] = useState('');
  const [examOn, setExamOn] = useState('');

  const record = useMutation({
    mutationFn: () => recordSkinExam(patientId, { icd10Code, examOn }),
    onSuccess: () => {
      setIcd10Code('');
      setExamOn('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('recall.heading')}</SectionHeading>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('recall.empty')}
        >
          {(statuses) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {statuses.map((status) => (
                <li
                  key={status.conditionId}
                  className="flex flex-col gap-1 rounded-control border border-line p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="m-0 font-medium text-ink">
                      {status.icd10} &mdash; {status.reason}
                    </p>
                    <p className="m-0 text-ink-muted">
                      {status.kind === 'surveillance'
                        ? t('recall.skinExamInterval')
                        : t('recall.anyVisitInterval')}
                      {t('recall.interval', { count: status.months })}
                      {status.dueDate === null ? '' : t('recall.due', { date: status.dueDate })}
                    </p>
                  </div>
                  <span
                    className={
                      'self-start rounded-pill px-2 py-0.5 text-xs font-medium sm:self-auto ' +
                      (status.state === 'overdue'
                        ? 'bg-danger-soft text-danger'
                        : status.state === 'due_soon'
                          ? 'bg-warning-soft text-warning'
                          : 'bg-canvas text-ink-muted')
                    }
                  >
                    {t(`recall.state.${recallStateKey(status.state)}`)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              record.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:items-end"
          >
            <Field
              label={t('recall.icd10Label')}
              value={icd10Code}
              onChange={(event) => {
                setIcd10Code(event.target.value);
              }}
              placeholder="C44.311"
              required
              className="sm:max-w-[12ch]"
            />
            <Field
              label={t('recall.examDateLabel')}
              type="date"
              value={examOn}
              onChange={(event) => {
                setExamOn(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={record.isPending}>
              {t('recall.recordExamButton')}
            </Button>
          </form>
        ) : null}
        {record.isError ? <ErrorText message={t('recall.recordError')} /> : null}
      </Panel>
    </section>
  );
}

// --- Patch testing -------------------------------------------------------------------------------

function PatchTestSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('dermatology');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['dermatology', 'patch-tests', patientId];

  const query = useQuery<readonly PatchTestPanelResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchPatchTestPanels(patientId, signal),
  });

  const [seriesName, setSeriesName] = useState('');
  const [appliedOn, setAppliedOn] = useState('');

  const createPanel = useMutation({
    mutationFn: () =>
      createPatchTestPanel(patientId, { seriesName, appliedOn, confidentialityReasons: [] }),
    onSuccess: () => {
      setSeriesName('');
      setAppliedOn('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('patchTest.heading')}</SectionHeading>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('patchTest.empty')}
        >
          {(panels) => (
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {panels.map((panel) => (
                <PatchTestPanelCard
                  key={panel.id}
                  patientId={patientId}
                  panel={panel}
                  mayWrite={mayWrite}
                />
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              createPanel.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:items-end"
          >
            <Field
              label={t('patchTest.seriesNameLabel')}
              value={seriesName}
              onChange={(event) => {
                setSeriesName(event.target.value);
              }}
              placeholder="T.R.U.E. Test"
              required
            />
            <Field
              label={t('patchTest.appliedOnLabel')}
              type="date"
              value={appliedOn}
              onChange={(event) => {
                setAppliedOn(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={createPanel.isPending}>
              {t('patchTest.startPanelButton')}
            </Button>
          </form>
        ) : null}
        {createPanel.isError ? <ErrorText message={t('patchTest.startError')} /> : null}
      </Panel>
    </section>
  );
}

function PatchTestPanelCard({
  patientId,
  panel,
  mayWrite,
}: {
  readonly patientId: string;
  readonly panel: PatchTestPanelResponse;
  readonly mayWrite: boolean;
}) {
  const { t } = useTranslation('dermatology');
  const queryClient = useQueryClient();
  const queryKey = ['dermatology', 'patch-tests', patientId];

  const [allergen, setAllergen] = useState('');
  const [readingDay, setReadingDay] = useState<string>(String(PATCH_READ_DAYS[0]));
  const [reading, setReading] = useState<string>(PATCH_READINGS[0]);
  const [readOn, setReadOn] = useState('');

  const addReading = useMutation({
    mutationFn: () => {
      const day = Number(readingDay);
      const parsedDay = PATCH_READ_DAYS.find((d) => d === day);
      if (parsedDay === undefined) throw new Error('invalid reading day');
      const parsedReading = PATCH_READINGS.find((r) => r === reading);
      if (parsedReading === undefined) throw new Error('invalid reading');
      return addPatchTestReading(patientId, panel.id, {
        allergen,
        readingDay: parsedDay,
        reading: parsedReading,
        readOn,
      });
    },
    onSuccess: () => {
      setAllergen('');
      setReadOn('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  // Raw per-day readings for one allergen, shown as supporting detail underneath the guarded
  // headline below — never as the only signal a reader sees. Grouped client-side purely for
  // display; the guard itself (reportableReading/isReportablePositive) already ran server-side in
  // `allergenResultsFor` (`server/src/dermatology/routes.ts`), and `panel.allergenResults` is that
  // computed, guarded answer — this component renders it, it does not re-derive it.
  const rawByAllergen = new Map<string, PatchTestPanelResponse['readings']>();
  for (const r of panel.readings) {
    rawByAllergen.set(r.allergen, [...(rawByAllergen.get(r.allergen) ?? []), r]);
  }

  return (
    <li className="rounded-control border border-line p-3 text-sm">
      <p className="m-0 font-medium text-ink">
        {panel.seriesName}
        {t('patchTest.appliedOn', { date: panel.appliedOn })}
      </p>
      {panel.allergenResults.length === 0 ? (
        <p className="m-0 mt-1 text-ink-muted">{t('patchTest.noReadings')}</p>
      ) : (
        <ul className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
          {panel.allergenResults.map((result) => (
            <li key={result.allergen} className="rounded-control border border-row-line p-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-ink">{result.allergen}</span>
                <span
                  className={
                    'rounded-pill px-2 py-0.5 text-xs font-medium ' +
                    (result.isReportablePositive
                      ? 'bg-danger-soft text-danger'
                      : 'bg-canvas text-ink-muted')
                  }
                >
                  {result.isReportablePositive
                    ? t('patchTest.positive', { reading: result.reportableReading })
                    : result.reportableReading === 'NT'
                      ? t('patchTest.notYetReportable')
                      : t('patchTest.negative', { reading: result.reportableReading })}
                </span>
              </div>
              <p className="m-0 mt-1 text-xs text-ink-muted">
                {t('patchTest.guardedResultNote')}{' '}
                {(rawByAllergen.get(result.allergen) ?? [])
                  .map((r) => t('patchTest.dayReading', { day: r.readingDay, reading: r.reading }))
                  .join(', ')}
              </p>
            </li>
          ))}
        </ul>
      )}

      {mayWrite ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            addReading.mutate();
          }}
          className="mt-3 flex flex-col gap-2 border-t border-row-line pt-3 sm:flex-row sm:flex-wrap sm:items-end"
        >
          <Field
            label={t('patchTest.allergenLabel')}
            value={allergen}
            onChange={(event) => {
              setAllergen(event.target.value);
            }}
            required
            className="sm:max-w-[20ch]"
          />
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink">{t('patchTest.dayFieldLabel')}</span>
            <select
              value={readingDay}
              onChange={(event) => {
                setReadingDay(event.target.value);
              }}
              className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
            >
              {PATCH_READ_DAYS.map((day) => (
                <option key={day} value={day}>
                  {t('patchTest.dayOption', { day })}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink">{t('patchTest.readingFieldLabel')}</span>
            <select
              value={reading}
              onChange={(event) => {
                setReading(event.target.value);
              }}
              className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
            >
              {PATCH_READINGS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <Field
            label={t('patchTest.readOnLabel')}
            type="date"
            value={readOn}
            onChange={(event) => {
              setReadOn(event.target.value);
            }}
            required
          />
          <Button type="submit" pending={addReading.isPending}>
            {t('patchTest.addReadingButton')}
          </Button>
        </form>
      ) : null}
      {addReading.isError ? <ErrorText message={t('patchTest.addReadingError')} /> : null}
    </li>
  );
}

// --- Destruction procedures ------------------------------------------------------------------

function DestructionSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('dermatology');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['dermatology', 'destruction-procedures', patientId];

  const query = useQuery<readonly DestructionProcedureResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchDestructionProcedures(patientId, signal),
  });

  const [lesionCount, setLesionCount] = useState('');
  const [bodySite, setBodySite] = useState('');
  const [performedOn, setPerformedOn] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createDestructionProcedure(patientId, {
        lesionCount: lesionCount === '' ? null : Number(lesionCount),
        bodySite: bodySite === '' ? undefined : bodySite,
        performedOn,
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setLesionCount('');
      setBodySite('');
      setPerformedOn('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('destructionProcedures.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('destructionProcedures.description')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('destructionProcedures.empty')}
        >
          {(procedures) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {procedures.map((procedure) => (
                <li key={procedure.id} className="rounded-control border border-line p-3 text-sm">
                  <p className="m-0 font-medium text-ink">
                    {procedure.performedOn}
                    {procedure.bodySite === null ? '' : ` — ${procedure.bodySite}`}
                    {procedure.lesionCount === null
                      ? ''
                      : t('destructionProcedures.lesionsCount', { count: procedure.lesionCount })}
                  </p>
                  {procedure.codingRefused ? (
                    <p className="m-0 mt-1 text-warning">
                      {t('destructionProcedures.codingRefused')}
                    </p>
                  ) : (
                    <ul className="m-0 mt-1 flex list-none flex-col gap-0.5 p-0 text-ink-muted">
                      {procedure.cptLines.map((line) => (
                        <li key={line.cpt}>
                          {line.cpt} &mdash; {line.display} &times; {line.units}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <Field
              label={t('destructionProcedures.lesionCountLabel')}
              type="number"
              min={1}
              value={lesionCount}
              onChange={(event) => {
                setLesionCount(event.target.value);
              }}
              hint={t('destructionProcedures.lesionCountHint')}
              className="sm:max-w-[10ch]"
            />
            <Field
              label={t('destructionProcedures.bodySiteLabel')}
              value={bodySite}
              onChange={(event) => {
                setBodySite(event.target.value);
              }}
            />
            <Field
              label={t('destructionProcedures.performedOnLabel')}
              type="date"
              value={performedOn}
              onChange={(event) => {
                setPerformedOn(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={create.isPending}>
              {t('destructionProcedures.recordProcedureButton')}
            </Button>
          </form>
        ) : null}
        {create.isError ? <ErrorText message={t('destructionProcedures.recordError')} /> : null}
      </Panel>
    </section>
  );
}
